import React, { useMemo, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Globe } from 'lucide-react';

// Language → approximate region centroid + display name
const LANG_REGIONS: Record<string, { name: string; country: string; lat: number; lng: number }> = {
  en: { name: 'English', country: 'Global (EN)', lat: 37.09, lng: -95.71 },
  es: { name: 'Spanish', country: 'Latin America / Spain', lat: 4.57, lng: -74.29 },
  pt: { name: 'Portuguese', country: 'Brazil / Portugal', lat: -14.23, lng: -51.92 },
  fr: { name: 'French', country: 'France / Africa', lat: 46.23, lng: 2.21 },
  de: { name: 'German', country: 'Germany / DACH', lat: 51.16, lng: 10.45 },
  it: { name: 'Italian', country: 'Italy', lat: 41.87, lng: 12.56 },
  ru: { name: 'Russian', country: 'Russia / CIS', lat: 61.52, lng: 105.31 },
  ar: { name: 'Arabic', country: 'Middle East / N. Africa', lat: 25.20, lng: 45.08 },
  hi: { name: 'Hindi', country: 'India', lat: 20.59, lng: 78.96 },
  zh: { name: 'Chinese', country: 'China', lat: 35.86, lng: 104.19 },
  ja: { name: 'Japanese', country: 'Japan', lat: 36.20, lng: 138.25 },
  ko: { name: 'Korean', country: 'South Korea', lat: 35.90, lng: 127.76 },
  tr: { name: 'Turkish', country: 'Turkey', lat: 38.96, lng: 35.24 },
  nl: { name: 'Dutch', country: 'Netherlands', lat: 52.13, lng: 5.29 },
  pl: { name: 'Polish', country: 'Poland', lat: 51.92, lng: 19.14 },
  id: { name: 'Indonesian', country: 'Indonesia', lat: -0.79, lng: 113.92 },
  fa: { name: 'Persian', country: 'Iran', lat: 32.42, lng: 53.68 },
  uk: { name: 'Ukrainian', country: 'Ukraine', lat: 48.38, lng: 31.16 },
  sv: { name: 'Swedish', country: 'Sweden', lat: 60.13, lng: 18.64 },
  he: { name: 'Hebrew', country: 'Israel', lat: 31.04, lng: 34.85 },
  th: { name: 'Thai', country: 'Thailand', lat: 15.87, lng: 100.99 },
  vi: { name: 'Vietnamese', country: 'Vietnam', lat: 14.05, lng: 108.27 },
};

const SENTIMENT_COLOR = {
  positive: '#22c55e',
  negative: '#ef4444',
  neutral: '#94a3b8',
};

interface SocialPost {
  id: string;
  platform: string;
  language: string;
  sentiment: 'positive' | 'negative' | 'neutral' | 'unknown';
  likes: number;
  shares: number;
  comments: number;
}

interface GeoHeatmapProps {
  posts: SocialPost[];
}

interface RegionData {
  lang: string;
  region: typeof LANG_REGIONS[string];
  count: number;
  percentage: number;
  sentiment: { positive: number; negative: number; neutral: number };
  engagement: number;
  dominantSentiment: 'positive' | 'negative' | 'neutral';
}

const GeoHeatmap: React.FC<GeoHeatmapProps> = ({ posts }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const regionData = useMemo<RegionData[]>(() => {
    const counts = new Map<string, { count: number; pos: number; neg: number; neu: number; engagement: number }>();

    posts.forEach(post => {
      const lang = (post.language || 'en').toLowerCase().split('-')[0];
      if (!counts.has(lang)) counts.set(lang, { count: 0, pos: 0, neg: 0, neu: 0, engagement: 0 });
      const entry = counts.get(lang)!;
      entry.count++;
      if (post.sentiment === 'positive') entry.pos++;
      else if (post.sentiment === 'negative') entry.neg++;
      else entry.neu++;
      entry.engagement += post.likes + post.shares + post.comments;
    });

    const total = posts.length || 1;
    return Array.from(counts.entries())
      .filter(([lang]) => LANG_REGIONS[lang])
      .map(([lang, data]) => {
        const dominant: 'positive' | 'negative' | 'neutral' =
          data.pos >= data.neg && data.pos >= data.neu ? 'positive' :
          data.neg >= data.neu ? 'negative' : 'neutral';
        return {
          lang,
          region: LANG_REGIONS[lang],
          count: data.count,
          percentage: (data.count / total) * 100,
          sentiment: { positive: data.pos, negative: data.neg, neutral: data.neu },
          engagement: data.engagement,
          dominantSentiment: dominant,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [posts]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || regionData.length === 0) return;

    const width = containerRef.current.clientWidth || 800;
    const height = 320;
    const maxCount = Math.max(...regionData.map(r => r.count), 1);

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    // Natural Earth projection
    const projection = d3.geoNaturalEarth1()
      .scale(width / 6.5)
      .translate([width / 2, height / 2]);

    const pathGen = d3.geoPath().projection(projection);

    const g = svg.append('g');

    // Load world topojson from CDN
    const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
    fetch(geoUrl)
      .then(r => r.json())
      .then(world => {
        // @ts-ignore
        const countries = (window as any).topojson
          ? // @ts-ignore
            (window as any).topojson.feature(world, world.objects.countries).features
          : null;

        // Draw graticule (grid lines)
        const graticule = d3.geoGraticule()();
        g.append('path')
          .datum(graticule)
          .attr('d', pathGen as any)
          .attr('fill', 'none')
          .attr('stroke', '#1e3a5f')
          .attr('stroke-width', 0.3)
          .attr('opacity', 0.6);

        // Draw sphere background
        g.append('path')
          .datum({ type: 'Sphere' } as any)
          .attr('d', pathGen as any)
          .attr('fill', '#0f172a');

        // Without topojson (not available), draw simple grid background
        if (!countries) {
          // Simple rectangular world backdrop
          g.append('rect')
            .attr('x', 0).attr('y', 0)
            .attr('width', width).attr('height', height)
            .attr('fill', '#0f172a');
        }

        // Plot bubbles for each language region
        regionData.forEach(rd => {
          const projected = projection([rd.region.lng, rd.region.lat]);
          if (!projected) return;
          const [px, py] = projected;
          const r = Math.max(8, Math.min(36, (rd.count / maxCount) * 36));
          const color = SENTIMENT_COLOR[rd.dominantSentiment];

          // Outer glow
          g.append('circle')
            .attr('cx', px).attr('cy', py)
            .attr('r', r + 4)
            .attr('fill', color)
            .attr('opacity', 0.15);

          // Main bubble
          g.append('circle')
            .attr('cx', px).attr('cy', py)
            .attr('r', r)
            .attr('fill', color)
            .attr('fill-opacity', 0.5)
            .attr('stroke', color)
            .attr('stroke-width', 1.5)
            .attr('stroke-opacity', 0.9);

          // Count label
          if (r > 10) {
            g.append('text')
              .attr('x', px).attr('y', py)
              .attr('dy', '0.35em')
              .attr('text-anchor', 'middle')
              .attr('font-size', r > 16 ? '11px' : '9px')
              .attr('font-weight', '700')
              .attr('fill', 'white')
              .text(rd.count);
          }

          // Language label below bubble
          g.append('text')
            .attr('x', px).attr('y', py + r + 12)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('fill', '#94a3b8')
            .text(rd.region.name);
        });
      })
      .catch(() => {
        // Fallback: just draw bubbles on dark rect background
        g.append('rect').attr('width', width).attr('height', height).attr('fill', '#0f172a');
        regionData.forEach(rd => {
          const projection2 = d3.geoEquirectangular().scale(width / 7).translate([width / 2, height / 2]);
          const projected = projection2([rd.region.lng, rd.region.lat]);
          if (!projected) return;
          const [px, py] = projected;
          const r = Math.max(8, Math.min(32, (rd.count / maxCount) * 32));
          const color = SENTIMENT_COLOR[rd.dominantSentiment];
          g.append('circle').attr('cx', px).attr('cy', py).attr('r', r)
            .attr('fill', color).attr('fill-opacity', 0.5)
            .attr('stroke', color).attr('stroke-width', 1.5);
          g.append('text').attr('x', px).attr('y', py).attr('dy', '0.35em')
            .attr('text-anchor', 'middle').attr('font-size', '10px').attr('fill', 'white').text(rd.count);
        });
      });
  }, [regionData]);

  const unknownLangs = useMemo(() => {
    const known = new Set(Object.keys(LANG_REGIONS));
    return [...new Set(posts.map(p => (p.language || '').toLowerCase().split('-')[0]).filter(Boolean))]
      .filter(l => !known.has(l));
  }, [posts]);

  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <div className="text-center">
          <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No posts to map</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* World map */}
      <div ref={containerRef} className="bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
        <svg ref={svgRef} className="w-full" />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-500 opacity-70" /> Positive dominant region</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500 opacity-70" /> Negative dominant region</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-slate-400 opacity-70" /> Neutral dominant region</div>
        <span className="ml-auto">Bubble size = post volume</span>
      </div>

      {/* Breakdown table */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Regional Breakdown</h3>
        <div className="space-y-2">
          {regionData.map(({ lang, region, count, percentage, sentiment, engagement }) => (
            <div key={lang} className="flex items-center gap-3">
              <div className="w-28 shrink-0">
                <div className="text-xs font-medium text-foreground">{region.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">{region.country}</div>
              </div>
              <div className="flex-1 flex h-3 rounded-full overflow-hidden bg-slate-800">
                {sentiment.positive > 0 && <div className="bg-green-500" style={{ width: `${(sentiment.positive / count) * 100}%` }} />}
                {sentiment.neutral > 0 && <div className="bg-slate-400" style={{ width: `${(sentiment.neutral / count) * 100}%` }} />}
                {sentiment.negative > 0 && <div className="bg-red-500" style={{ width: `${(sentiment.negative / count) * 100}%` }} />}
              </div>
              <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
              <span className="text-xs text-muted-foreground w-10 text-right">{percentage.toFixed(1)}%</span>
              <span className="text-xs text-muted-foreground w-24 text-right hidden lg:block">{engagement.toLocaleString()} eng.</span>
            </div>
          ))}
        </div>
        {unknownLangs.length > 0 && (
          <p className="text-xs text-muted-foreground pt-1">Not mapped: {unknownLangs.join(', ')}</p>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800 text-center">
          <div className="text-2xl font-bold">{regionData.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Regions detected</div>
        </div>
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800 text-center">
          <div className="text-lg font-bold truncate">{regionData[0]?.region.name || '—'}</div>
          <div className="text-xs text-muted-foreground mt-1">Top language region</div>
        </div>
        <div className="bg-slate-900 rounded-lg p-3 border border-slate-800 text-center">
          <div className="text-2xl font-bold">
            {regionData.length > 0
              ? `${Math.round(regionData.filter(r => r.dominantSentiment === 'positive').length / regionData.length * 100)}%`
              : '—'}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Regions net positive</div>
        </div>
      </div>
    </div>
  );
};

export default GeoHeatmap;
